import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { HiOutlineMenuAlt3, HiX, HiChevronDown, HiPhone, HiMail, HiLocationMarker } from 'react-icons/hi';
import { FaWhatsapp, FaInstagram } from 'react-icons/fa';
import { COMPANY } from '../../data/siteData';
import logoImg from '../../assets/logo.png';

const destinationItems = [
  { name: 'Study in UK', flag: '🇬🇧', slug: 'uk' },
  { name: 'Study in USA', flag: '🇺🇸', slug: 'usa' },
  { name: 'Study in Canada', flag: '🇨🇦', slug: 'canada' },
  { name: 'Study in Australia', flag: '🇦🇺', slug: 'australia' },
  { name: 'Study in Germany', flag: '🇩🇪', slug: 'germany' },
  { name: 'Study in Switzerland', flag: '🇨🇭', slug: 'switzerland' },
  { name: 'Study in Ireland', flag: '🇮🇪', slug: 'ireland' },
];

const serviceItems = [
  { name: 'Visa Assistance', icon: '🛂', link: '/services/visa-assistance' },
  { name: 'Loan Assistance', icon: '🏦', link: '/services/loan-assistance' },
  { name: 'SOP Assistance', icon: '✍️', link: '/services/sop-assistance' },
  { name: 'Scholarship Guidance', icon: '🏆', link: '/services/scholarship-assistance' },
  { name: 'Course Selection', icon: '🎯', link: '/services' },
  { name: 'University Application', icon: '🏛️', link: '/services' },
  { name: 'Pre-Departure Briefing', icon: '✈️', link: '/services' },
  { name: 'Work Permit Assistance', icon: '📋', link: '/services' },
];

const resourceItems = [
  { name: 'Study Abroad Guide', icon: '📖', link: '/guide' },
  { name: 'Country Cost Calculator', icon: '💱', link: '/calculator' },
  { name: 'Visa Checklist', icon: '✅', link: '/visa' },
  { name: 'Blog & News', icon: '📰', link: '/blog' },
];

const navLinks = [
  { name: 'Destinations', path: '/destinations', hasMega: 'destinations' },
  { name: 'Services', path: '/services', hasMega: 'services' },
  { name: 'About Us', path: '/about', hasMega: false },
  { name: 'Resources', path: '/resources', hasMega: 'resources' },
  { name: 'Contact', path: '/contact', hasMega: false },
];

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [activeMega, setActiveMega] = useState(null);
  const location = useLocation();

  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 20);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setIsMobileOpen(false);
    setActiveMega(null);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = isMobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMobileOpen]);

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <>
      {/* Top bar */}
      <div className="hidden lg:block bg-primary text-white/70 text-xs">
        <div className="container-custom flex items-center justify-between py-2">
          <div className="flex items-center gap-1.5">
            <HiLocationMarker className="w-3.5 h-3.5 text-secondary" />
            <span>{COMPANY.address}</span>
          </div>
          <div className="flex items-center gap-5">
            <a href={`tel:${COMPANY.phone}`} className="flex items-center gap-1.5 hover:text-white transition-colors">
              <HiPhone className="w-3.5 h-3.5 text-secondary" />
              {COMPANY.phone}
            </a>
            <a href={`mailto:${COMPANY.email}`} className="flex items-center gap-1.5 hover:text-white transition-colors">
              <HiMail className="w-3.5 h-3.5 text-secondary" />
              {COMPANY.email}
            </a>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className={`sticky top-0 left-0 right-0 z-50 transition-all duration-500 ${isScrolled
          ? 'bg-white/95 backdrop-blur-2xl border-b border-gray-200/60 shadow-md'
          : 'bg-white border-b border-gray-100'
          }`}
      >
        <div className="container-custom">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group flex-shrink-0">
              <div className="h-12 md:h-16 w-auto flex items-center transition-transform duration-300 group-hover:scale-105">
                <img src={logoImg} alt="MasterTech Logo" className="h-full w-auto object-contain" />
              </div>
              <div className="flex flex-col leading-none">
                <span className="text-base font-black text-primary tracking-tight">MasterTech</span>
                <span className="text-[10px] font-semibold text-secondary tracking-widest uppercase mt-0.5">Educational</span>
              </div>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-0.5">
              {navLinks.map((link) => (
                <div
                  key={link.name}
                  className="relative group"
                  onMouseEnter={() => link.hasMega && setActiveMega(link.hasMega)}
                  onMouseLeave={() => setActiveMega(null)}
                >
                  <Link
                    to={link.path}
                    className={`flex items-center gap-1 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${isActive(link.path)
                      ? 'text-secondary bg-secondary/5'
                      : 'text-primary/80 hover:text-primary hover:bg-gray-100/70'
                      }`}
                  >
                    {link.name}
                    {link.hasMega && (
                      <HiChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${activeMega === link.hasMega ? 'rotate-180' : ''}`} />
                    )}
                  </Link>

                  {isActive(link.path) && (
                    <motion.div
                      layoutId="nav-indicator"
                      className="absolute bottom-0 left-4 right-4 h-0.5 bg-secondary rounded-full"
                      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                    />
                  )}

                  {/* Destinations Mega Menu */}
                  {link.hasMega === 'destinations' && (
                    <AnimatePresence>
                      {activeMega === 'destinations' && (
                        <motion.div
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 8 }}
                          transition={{ duration: 0.18 }}
                          className="absolute top-full left-0 w-72 bg-white rounded-2xl border border-gray-200/60 shadow-elevated p-4 mt-1"
                        >
                          <p className="text-[10px] font-bold text-text-muted uppercase tracking-widest px-3 mb-2">Study Destinations</p>
                          <div className="space-y-0.5">
                            {destinationItems.map((dest) => (
                              <Link
                                key={dest.slug}
                                to={`/destinations/${dest.slug}`}
                                className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-secondary/5 transition-colors group/item"
                              >
                                <span className="text-xl">{dest.flag}</span>
                                <span className="text-sm font-medium text-primary/80 group-hover/item:text-secondary transition-colors">
                                  {dest.name}
                                </span>
                              </Link>
                            ))}
                          </div>
                          <Link
                            to="/destinations"
                            className="mt-2 pt-2 border-t border-gray-100 flex items-center gap-2 px-3 text-sm font-bold text-secondary hover:gap-3 transition-all"
                          >
                            All Destinations →
                          </Link>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  )}

                  {/* Services Mega Menu */}
                  {link.hasMega === 'services' && (
                    <AnimatePresence>
                      {activeMega === 'services' && (
                        <motion.div
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 8 }}
                          transition={{ duration: 0.18 }}
                          className="absolute top-full left-0 w-80 bg-white rounded-2xl border border-gray-200/60 shadow-elevated p-4 mt-1"
                        >
                          <p className="text-[10px] font-bold text-text-muted uppercase tracking-widest px-3 mb-2">Our Services</p>
                          <div className="grid grid-cols-2 gap-0.5">
                            {serviceItems.map((item) => (
                              <Link
                                key={item.name}
                                to={item.link}
                                className="flex items-center gap-2 px-3 py-2.5 rounded-xl hover:bg-secondary/5 transition-colors group/item"
                              >
                                <span className="text-base">{item.icon}</span>
                                <span className="text-xs font-medium text-primary/80 group-hover/item:text-secondary transition-colors leading-tight">
                                  {item.name}
                                </span>
                              </Link>
                            ))}
                          </div>
                          <Link
                            to="/services"
                            className="mt-2 pt-2 border-t border-gray-100 flex items-center gap-2 px-3 text-sm font-bold text-secondary hover:gap-3 transition-all"
                          >
                            View All Services →
                          </Link>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  )}

                  {/* Resources Mega Menu */}
                  {link.hasMega === 'resources' && (
                    <AnimatePresence>
                      {activeMega === 'resources' && (
                        <motion.div
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 8 }}
                          transition={{ duration: 0.18 }}
                          className="absolute top-full left-0 w-64 bg-white rounded-2xl border border-gray-200/60 shadow-elevated p-4 mt-1"
                        >
                          <p className="text-[10px] font-bold text-text-muted uppercase tracking-widest px-3 mb-2">Resources</p>
                          <div className="space-y-0.5">
                            {resourceItems.map((item) => (
                              <Link
                                key={item.name}
                                to={item.link}
                                className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-secondary/5 transition-colors group/item"
                              >
                                <span className="text-base">{item.icon}</span>
                                <span className="text-sm font-medium text-primary/80 group-hover/item:text-secondary transition-colors">
                                  {item.name}
                                </span>
                              </Link>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  )}
                </div>
              ))}
            </div>

            {/* CTA + Mobile Toggle */}
            <div className="flex items-center gap-3">
              <Link
                to="/apply"
                className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-sm text-white transition-all duration-300 hover:scale-[1.03] hover:shadow-glow-accent whitespace-nowrap"
                style={{ background: 'var(--gradient-cta)' }}
              >
                Free Consultation →
              </Link>

              <button
                onClick={() => setIsMobileOpen(!isMobileOpen)}
                className="lg:hidden w-10 h-10 flex items-center justify-center rounded-xl hover:bg-gray-100 transition-colors"
                aria-label="Toggle menu"
              >
                {isMobileOpen ? (
                  <HiX className="w-6 h-6 text-primary" />
                ) : (
                  <HiOutlineMenuAlt3 className="w-6 h-6 text-primary" />
                )}
              </button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 lg:hidden"
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black"
              onClick={() => setIsMobileOpen(false)}
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className="absolute right-0 top-0 bottom-0 w-full max-w-sm bg-white shadow-2xl flex flex-col"
            >
              {/* Mobile header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                <Link to="/" className="flex items-center gap-3" onClick={() => setIsMobileOpen(false)}>
                  <div className="h-10 w-auto flex items-center justify-start">
                    <img src={logoImg} alt="MasterTech Logo" className="h-full w-auto object-contain" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm font-black text-primary leading-tight">MasterTech</span>
                    <span className="text-[9px] font-bold text-secondary uppercase tracking-widest leading-none">Educational</span>
                  </div>
                </Link>
                <button onClick={() => setIsMobileOpen(false)} className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-gray-100">
                  <HiX className="w-5 h-5 text-primary" />
                </button>
              </div>

              {/* Nav links */}
              <div className="flex-1 overflow-y-auto py-4 px-4">
                {navLinks.map((link, i) => (
                  <motion.div
                    key={link.name}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.05 + i * 0.06 }}
                  >
                    <Link
                      to={link.path}
                      className={`flex items-center justify-between px-4 py-3.5 rounded-xl text-base font-semibold transition-all ${isActive(link.path)
                        ? 'text-secondary bg-secondary/5'
                        : 'text-primary/80 hover:text-primary hover:bg-gray-50'
                        }`}
                    >
                      {link.name}
                      {link.hasMega && <HiChevronDown className="w-4 h-4 opacity-40" />}
                    </Link>
                  </motion.div>
                ))}

                {/* Destinations quick links */}
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-[10px] font-bold text-text-muted uppercase tracking-widest px-4 mb-2">Destinations</p>
                  <div className="grid grid-cols-2 gap-1">
                    {destinationItems.map((dest) => (
                      <Link
                        key={dest.slug}
                        to={`/destinations/${dest.slug}`}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-secondary/5 transition-colors"
                      >
                        <span className="text-lg">{dest.flag}</span>
                        <span className="text-xs font-medium text-primary/70">{dest.name.replace('Study in ', '')}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              {/* Mobile CTA */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 }}
                className="px-4 pb-6 pt-4 border-t border-gray-100 space-y-3"
              >
                <Link
                  to="/apply"
                  className="flex items-center justify-center gap-2 w-full px-6 py-3.5 rounded-xl font-bold text-white text-base"
                  style={{ background: 'var(--gradient-cta)' }}
                >
                  Free Consultation →
                </Link>
                <div className="flex gap-3">
                  <a
                    href={`tel:${COMPANY.phone}`}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold text-secondary border-2 border-secondary text-sm"
                  >
                    <HiPhone className="w-4 h-4" /> Call Us
                  </a>
                  <a
                    href={`https://wa.me/${COMPANY.whatsapp}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold text-green-600 border-2 border-green-500 text-sm"
                  >
                    <FaWhatsapp className="w-4 h-4" /> WhatsApp
                  </a>
                </div>
                {/* Social */}
                <div className="flex items-center justify-center gap-4 pt-2">
                  {[
                    { icon: FaInstagram, href: COMPANY.social.instagram, label: 'Instagram' },
                  ].map(({ icon: Icon, href, label }) => (
                    <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label}
                      className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-secondary/10 hover:text-secondary text-text-muted transition-colors">
                      <Icon className="w-4 h-4" />
                    </a>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* WhatsApp floating button */}
      <motion.a
        href={`https://wa.me/${COMPANY.whatsapp}?text=Hi%20MasterTech%2C%20I%20want%20to%20know%20more%20about%20study%20abroad%20options.`}
        target="_blank"
        rel="noopener noreferrer"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 2, type: 'spring', stiffness: 200 }}
        whileHover={{ scale: 1.1 }}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-green-500 rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-shadow"
        aria-label="Chat on WhatsApp"
      >
        <FaWhatsapp className="w-7 h-7 text-white" />
      </motion.a>
    </>
  );
};

export default Navbar;
